import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  Search,
  Filter,
  Calendar,
  User,
  Eye,
  Package,
  ChevronRight,
  RefreshCw,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle
} from 'lucide-react';
import { API_URL } from '../../Config';

export default function ToutesDemandes() {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [error, setError] = useState('');

  const token = localStorage.getItem('token');
  const headers = token ? { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  } : {};

  useEffect(() => {
    fetchDemandes();
  }, [filterStatus, dateDebut, dateFin]);

  const fetchDemandes = async () => {
    try {
      setLoading(true);
      setError('');
      
      let url = `${API_URL}/demandes?per_page=50`;
      const params = new URLSearchParams();
      
      if (filterStatus) {
        params.append('status', filterStatus);
      }
      if (dateDebut) {
        params.append('date_debut', dateDebut);
      }
      if (dateFin) {
        params.append('date_fin', dateFin);
      }
      
      if (params.toString()) {
        url += `&${params.toString()}`;
      }
      
      console.log('Fetching demandes from:', url);
      
      const response = await axios.get(url, { headers });
      console.log('Response data:', response.data);
      
      // Structure de réponse de votre API
      const responseData = response.data.data;
      let demandesData = [];
      
      if (responseData?.data) {
        // Structure: { data: { data: [...] } }
        demandesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        // Structure: [...]
        demandesData = responseData;
      } else if (Array.isArray(response.data.data)) {
        // Alternative structure
        demandesData = response.data.data;
      }
      
      setDemandes(demandesData || []);
    } catch (err) {
      console.error('Erreur lors du chargement des demandes:', err);
      
      if (err.response) {
        if (err.response.status === 404) {
          setError('Route API non trouvée. Vérifiez le chemin.');
        } else if (err.response.status === 401) {
          setError('Non authentifié. Veuillez vous reconnecter.');
        } else {
          setError(`Erreur serveur: ${err.response.status}`);
        }
      } else if (err.request) {
        setError('Serveur inaccessible. Vérifiez votre connexion.');
      } else {
        setError('Erreur de configuration: ' + err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      en_attente: {
        label: 'En attente',
        color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
        icon: Clock,
        dotColor: 'bg-yellow-500'
      },
      validee: {
        label: 'Validée',
        color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
        icon: CheckCircle,
        dotColor: 'bg-green-500'
      },
      refusee: {
        label: 'Refusée',
        color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
        icon: XCircle,
        dotColor: 'bg-red-500'
      },
      annulee: {
        label: 'Annulée',
        color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        icon: AlertCircle,
        dotColor: '0'
      },
      pret_recuperation: {
        label: 'Prêt à récupérer',
        color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
        icon: Package,
        dotColor: 'bg-blue-500'
      },
      en_preparation: {
        label: 'En préparation',
        color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300',
        icon: Package,
        dotColor: 'bg-indigo-500'
      }
    };
    
    return statusMap[status] || {
      label: status || 'Inconnu',
      color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      icon: AlertCircle,
      dotColor: 'bg-gray-400'
    };
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Date inconnue';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'Date invalide';
    }
  };

  const filteredDemandes = demandes.filter(demande => {
    // Filtre par recherche
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        (demande.numero && demande.numero.toLowerCase().includes(searchLower)) ||
        (demande.id && demande.id.toString().includes(searchTerm)) ||
        (demande.demandeur?.nom && demande.demandeur.nom.toLowerCase().includes(searchLower)) ||
        (demande.demandeur?.prenom && demande.demandeur.prenom.toLowerCase().includes(searchLower)) ||
        (demande.demandeur?.email && demande.demandeur.email.toLowerCase().includes(searchLower))
      );
    }
    return true;
  });

  if (loading && demandes.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-300 text-lg">Chargement des demandes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
            Toutes les demandes
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Gestion et suivi de toutes les demandes de matériel
          </p>
        </div>

        {/* Message d'erreur */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-500" />
              <p className="text-red-700 dark:text-red-300">{error}</p>
              <button
                onClick={() => setError('')}
                className="ml-auto text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Filtres et recherche */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Barre de recherche */}
            <div className="md:col-span-1">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3  dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Filtre statut */}
            <div>
              <div className="relative">
                <Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="w-full pl-12 pr-4 py-3  dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-900 dark:text-white appearance-none"
                >
                  <option value="">Tous les statuts</option>
                  <option value="en_attente">En attente</option>
                  <option value="validee">Validée</option>
                  <option value="refusee">Refusée</option>
                  <option value="annulee">Annulée</option>
                  <option value="pret_recuperation">Prêt à récupérer</option>
                  <option value="en_preparation">En préparation</option>
                </select>
              </div>
            </div>

            {/* Date début */}
            <div>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={dateDebut}
                  onChange={(e) => setDateDebut(e.target.value)}
                  className="w-full pl-12 pr-4 py-3  dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-900 dark:text-white"
                  placeholder="Date début"
                />
              </div>
            </div>

            {/* Date fin */}
            <div>
              <div className="relative">
                <Calendar className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="date"
                  value={dateFin}
                  onChange={(e) => setDateFin(e.target.value)}
                  className="w-full pl-12 pr-4 py-3  dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-gray-900 dark:text-white"
                  placeholder="Date fin"
                />
              </div>
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => {
                setFilterStatus('');
                setDateDebut('');
                setDateFin('');
                setSearchTerm('');
              }}
              className="px-6 py-3 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 font-semibold rounded-lg transition-colors"
            >
              Réinitialiser
            </button>
            
            <button
              onClick={fetchDemandes}
              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Actualiser
            </button>
          </div>
        </div>

        {/* Tableau des demandes */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* En-tête du tableau */}
          <div className="hidden md:grid grid-cols-12 gap-4 p-4 md:p-6 border-b border-gray-200 dark:border-gray-700  dark:bg-gray-900/50 font-semibold text-gray-700 dark:text-gray-300 text-sm">
            <div className="col-span-1">N°</div>
            <div className="col-span-3">Demandeur</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2">Statut</div>
            <div className="col-span-2">Matériels</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Corps du tableau */}
          {filteredDemandes.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-400 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                {searchTerm || filterStatus || dateDebut || dateFin 
                  ? 'Aucune demande ne correspond aux critères' 
                  : 'Aucune demande disponible'}
              </p>
              {demandes.length === 0 && !loading && (
                <button
                  onClick={fetchDemandes}
                  className="mt-4 px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Réessayer
                </button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredDemandes.map((demande) => {
                const statusInfo = getStatusInfo(demande.status);
                const StatusIcon = statusInfo.icon;
                const articles = demande.demande_materiels || [];
                const validateur = demande.validateur?.nom || 'Non validée';
                
                return (
                  <div key={demande.id} className="p-4 md:p-6 hover: dark:hover:bg-gray-900/50 transition-colors">
                    {/* Version mobile */}
                    <div className="md:hidden space-y-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-mono font-bold text-blue-600 dark:text-blue-400">
                            #{demande.numero || demande.id}
                          </div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {formatDate(demande.created_at)}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 dark:text-white">
                            {demande.demandeur?.nom || 'Utilisateur'} {demande.demandeur?.prenom || ''}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            Validateur: {validateur}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span className="text-gray-900 dark:text-white">
                            {articles.length} article(s)
                          </span>
                        </div>
                        
                        <Link
                          to={`/demande-timeline/${demande.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg font-medium transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Suivi
                        </Link>
                      </div>
                    </div>

                    {/* Version desktop */}
                    <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                      {/* Numéro */}
                      <div className="col-span-1">
                        <div className="font-mono font-bold text-blue-600 dark:text-blue-400">
                          #{demande.numero || demande.id}
                        </div>
                      </div>

                      {/* Demandeur */}
                      <div className="col-span-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900 dark:text-white">
                              {demande.demandeur?.nom || 'Utilisateur'} {demande.demandeur?.prenom || ''}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              Validateur: {validateur}
                            </div>
                            {demande.demandeur?.email && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                {demande.demandeur.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Date */}
                      <div className="col-span-2">
                        <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{formatDate(demande.created_at)}</span>
                        </div>
                        {demande.date_validation && (
                          <div className="text-xs text-green-600 dark:text-green-400 mt-1">
                            Validé: {formatDate(demande.date_validation)}
                          </div>
                        )}
                      </div>

                      {/* Statut */}
                      <div className="col-span-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${statusInfo.dotColor}`}></div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                      </div>

                      {/* Matériels */}
                      <div className="col-span-2">
                        <div className="flex items-center gap-2">
                          <Package className="w-4 h-4 text-gray-400" />
                          <span className="font-medium text-gray-900 dark:text-white">
                            {articles.length}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 text-sm">article(s)</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Total: {articles.reduce((sum, item) => sum + (item.quantite_demandee || 0), 0)} unités
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="col-span-2 text-right">
                        <Link
                          to={`/demande-timeline/${demande.id}`}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg font-medium transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                          Voir le suivi
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Résumé */}
        {filteredDemandes.length > 0 && (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            {filteredDemandes.length} demande{filteredDemandes.length > 1 ? 's' : ''} affichée{filteredDemandes.length > 1 ? 's' : ''}
            {searchTerm && ` (recherche: "${searchTerm}")`}
            {(filterStatus || dateDebut || dateFin) && ' • Filtres actifs'}
          </div>
        )}
      </div>
    </div>
  );
}